import { motion } from "framer-motion";
import FeatureCard from "./FeatureCard";
import {
  RiBriefcaseLine,
  RiUserStarLine,
  RiRocketLine,
  RiTeamLine,
  RiTrophyLine,
  RiCodeSSlashLine,
} from "react-icons/ri";
import { useTheme } from "../Theme/ThemeContext";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

export default function Features() {
  const { currentTheme } = useTheme();

  const features = [
    {
      icon: <RiCodeSSlashLine className="text-white text-2xl" />,
      title: "Real-time Code Execution",
      description: "Write and run Golang code with instant feedback and test results.",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      icon: <RiTrophyLine className="text-white text-2xl" />,
      title: "Hidden Test Case Analysis",
      description: "Practice with hidden test cases to prepare for real interviews.",
      gradient: "from-orange-500 to-amber-500",
    },
    {
      icon: <RiUserStarLine className="text-white text-2xl" />,
      title: "Theory + Videos + MCQs",
      description: "Comprehensive learning with multiple formats for every topic.",
      gradient: "from-purple-500 to-pink-500",
    },
    {
      icon: <RiBriefcaseLine className="text-white text-2xl" />,
      title: "Job & Internship Portal",
      description: "Access verified job listings and referral opportunities.",
      gradient: "from-green-500 to-emerald-500",
    },
    {
      icon: <RiTeamLine className="text-white text-2xl" />,
      title: "Placement Support",
      description: "Resume writing, interview prep, and career guidance.",
      gradient: "from-indigo-500 to-blue-500",
    },
    {
      icon: <RiRocketLine className="text-white text-2xl" />,
      title: "From 0 to Hero",
      description: "Structured path from beginner to job-ready Golang developer.",
      gradient: "from-yellow-500 to-orange-500",
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-neutral-50 dark:from-neutral-900 dark:to-neutral-800 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-72 h-72 rounded-full filter blur-3xl opacity-40 -translate-x-1/2 -translate-y-1/2" style={{ background: `rgba(var(--color-primary), 0.2)` }}></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full filter blur-3xl opacity-40 translate-x-1/2 translate-y-1/2" style={{ background: `rgba(var(--color-secondary), 0.2)` }}></div>
      
      <div className="ca-container relative">
        {/* Section header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-theme-primary/10 rounded-full text-theme-primary text-sm font-semibold mb-4">
            <span className="w-2 h-2 bg-theme-primary rounded-full animate-pulse"></span>
            Why Choose Us
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-neutral-900 dark:text-white">
            Why Choose <span className={`bg-gradient-to-r ${currentTheme.colors.gradientText} bg-clip-text text-transparent`}>CodeArena?</span>
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 text-lg max-w-2xl mx-auto leading-relaxed">
            The only Golang bootcamp with real-time code execution, hidden test cases, and complete placement support.
          </p>
        </div>
        
        {/* Features grid */}
        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {features.map((f, i) => (
            <motion.div key={i} variants={itemVariants}>
              <FeatureCard {...f} index={i} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
